import { requestData, requestPage, type Page, type PageQuery } from '@/lib/api-client';
import type { CounterProduct, CounterSale, CreateSaleInput, DayBook, IsoDate, SaleListRow, Uuid } from './types';

const base = (pharmacyId: Uuid) => `/pharmacies/${pharmacyId}`;

/**
 * The product box on the sale screen.
 *
 * Returns each medicine with the batches actually on the shelf, earliest expiry
 * first — the counter picks a physical box, not a catalog entry.
 */
export function searchCounter(
  pharmacyId: Uuid,
  q: string,
  limit = 10,
  /** False also returns products with no stock, stocked first, so they can be sold short. */
  inStockOnly = true,
  offset = 0,
): Promise<CounterProduct[]> {
  return requestData<CounterProduct[]>(`${base(pharmacyId)}/counter/search`, {
    query: { q, limit, inStockOnly: String(inStockOnly), offset },
  });
}

export type SaleQuery = PageQuery & {
  q?: string;
  from?: string;
  to?: string;
  status?: 'COMPLETED' | 'CANCELLED';
};

export function listSales(pharmacyId: Uuid, query: SaleQuery = {}): Promise<Page<SaleListRow>> {
  return requestPage<SaleListRow>(`${base(pharmacyId)}/sales`, { query });
}

export function getSale(pharmacyId: Uuid, saleId: Uuid): Promise<CounterSale> {
  return requestData<CounterSale>(`${base(pharmacyId)}/sales/${saleId}`);
}

export function getDayBook(pharmacyId: Uuid, date?: string): Promise<DayBook> {
  return requestData<DayBook>(`${base(pharmacyId)}/sales/day-book`, { query: { date } });
}

/**
 * Rings up a bill.
 *
 * The idempotency key is generated per bill, not per attempt: a till on a poor
 * connection retries, and without it the stock leaves the shelf twice and the
 * customer is handed two invoice numbers for one bag.
 */
export function createSale(
  pharmacyId: Uuid,
  body: CreateSaleInput,
  idempotencyKey: string,
  /** Milliseconds to wait before giving up and queueing instead. */
  timeoutMs?: number,
): Promise<CounterSale> {
  return requestData<CounterSale>(`${base(pharmacyId)}/sales`, {
    method: 'POST',
    body,
    headers: { 'idempotency-key': idempotencyKey },
    timeoutMs,
  });
}

export function cancelSale(pharmacyId: Uuid, saleId: Uuid, reason: string): Promise<CounterSale> {
  return requestData<CounterSale>(`${base(pharmacyId)}/sales/${saleId}/cancel`, {
    method: 'POST',
    body: { reason },
  });
}

/** A name this shop has billed before. `phone` is null for a doctor. */
export type NameSuggestion = {
  name: string;
  phone: string | null;
  lastSeen: IsoDate;
  /** How many bills carry this name. What the list is ordered by. */
  bills: number;
};

/**
 * Names off this pharmacy's own past bills.
 *
 * Both till fields are free text on the bill rather than references to
 * anything, so this is the only honest source of suggestions — and pointedly
 * NOT the platform's customer directory, which would tell a shop about people
 * who have never walked through its door.
 */
export function suggestNames(
  pharmacyId: Uuid,
  field: 'customer' | 'doctor',
  q: string,
  limit = 10,
): Promise<NameSuggestion[]> {
  return requestData<NameSuggestion[]>(`/pharmacies/${pharmacyId}/sales/suggest`, {
    query: { field, q, limit },
  });
}

/** Everything the till needs to keep working with no network. */
export type CounterBootstrap = {
  capturedAt: IsoDate;
  billing: { prefix: string; financialYear: string; lastNumber: number };
  shelf: CounterProduct[];
};

/**
 * One call, taken on load and again on every reconnect.
 *
 * A till that needs five requests to become usable is unusable exactly when the
 * queue is worst — so where the bill series stands and what is on the shelf
 * arrive together or not at all.
 */
export function bootstrapCounter(pharmacyId: Uuid): Promise<CounterBootstrap> {
  return requestData<CounterBootstrap>(`${base(pharmacyId)}/counter/bootstrap`);
}
